import { ChoiceChips, Select, Switch } from '@platform/design-system';
import { useFormContext, useWatch } from 'react-hook-form';
import { subsidiaryOptions, type Subsidiary } from '../settings/subsidiaries.ts';
import { usePageContext } from '../usePageContext.ts';
import {
  grantablePermissions,
  grantablePresets,
  PERMISSION_GROUPS,
  permissionKeyOf,
  type AccessFormValues,
  type AccessPreset,
} from './team.ts';
import styles from './Team.module.css';

export type AccessFieldsProps = { subsidiaries: readonly Subsidiary[]; isAdmin: boolean };

export const AccessFields = ({ subsidiaries, isAdmin }: AccessFieldsProps) => {
  const { t, locale } = usePageContext();
  const form = useFormContext<AccessFormValues>();
  const preset = useWatch({ control: form.control, name: 'preset' });
  const currentSubsidiary = useWatch({ control: form.control, name: 'subsidiaryId' });
  const allowed = grantablePermissions(isAdmin);
  const choices = subsidiaryOptions(subsidiaries, currentSubsidiary);

  return (
    <>
      <ChoiceChips<AccessPreset>
        legend={t('team.access')}
        locale={locale}
        value={preset}
        helperText={t(`team.presetHelp_${preset}`)}
        choices={grantablePresets(isAdmin).map((value) => ({ value, label: t(`team.preset_${value}`) }))}
        onChange={(value) => {
          form.setValue('preset', value, { shouldDirty: true });
        }}
      />
      <Select
        label={t('team.subsidiary')}
        options={[{ value: '', label: t('team.noSubsidiary') }, ...choices]}
        {...form.register('subsidiaryId')}
      />
      <details className={styles['extra']}>
        <summary>{t('team.extra')}</summary>
        <p className={styles['help']}>{t('team.extraHelp')}</p>
        {PERMISSION_GROUPS.map((group) => (
          <fieldset key={group.id} className={styles['group']}>
            <legend>{t(`team.group_${group.id}`)}</legend>
            {group.permissions
              .filter((permission) => allowed.includes(permission))
              .map((permission) => (
                <Switch
                  key={permission}
                  label={t(permissionKeyOf(permission))}
                  value={permission}
                  {...form.register('permissions')}
                />
              ))}
          </fieldset>
        ))}
      </details>
    </>
  );
};
